import type { ObjectDirective, DirectiveBinding } from 'vue'
import cardFormatUtils from '~/utils/format/expiry'

export const formatCardExpiry = {
  bind: function bind (el:HTMLElement) {
    const element = el.nodeName.toLowerCase() === 'input' ? el : el.querySelector('input')
    if (!element) {
      return
    }

    element.addEventListener('keypress', cardFormatUtils.restrictNumeric)
    element.addEventListener('keypress', cardFormatUtils.restrictExpiry)
    element.addEventListener('keypress', cardFormatUtils.formatExpiry)
    element.addEventListener('keypress', cardFormatUtils.formatForwardSlashAndSpace)
    element.addEventListener('keypress', cardFormatUtils.formatForwardExpiry)
    element.addEventListener('keydown', cardFormatUtils.formatBackExpiry)
    element.addEventListener('change', cardFormatUtils.reFormatExpiry)
    element.addEventListener('input', cardFormatUtils.reFormatExpiry)
  }
}

/**
 * v-new-tab directive
 *
 * Appends a visually-hidden "opens in new tab" hint to any `<a target="_blank">`
 * element so screen reader users know a new tab will open before they activate
 * the link. The hint is rendered via a `.sr-only` span so it participates in
 * the accessibility tree without altering the visible label.
 *
 * Usage:
 * - `<a v-new-tab="$t('a11y.link.opensInNewTab')" target="_blank" ...>` — the
 *   hint text is passed in via `binding.value` so it is localized (story 62648).
 *   Every call site must use this form; screen-reader text is user-facing
 *   content and is translated like any other string.
 * - `SBtn` carries the directive on its own VBtn root, so SBtn consumers must
 *   NOT write `v-new-tab` themselves — a directive on a component tag never
 *   reaches the nested anchor and is silently inert (see
 *   `app/components/tests/sbtn-new-tab.spec.ts`).
 *
 * `NEW_TAB_LABEL` is retained as an English last-resort fallback only, used
 * when `binding.value` is falsy.
 *
 * The hint span is marked with `data-new-tab-hint="true"`, which is the
 * idempotency key — re-running `applyNewTab` against the same anchor will not
 * append a second hint, even if other `.sr-only` siblings are present or the
 * hint is not the last child.
 *
 * The `.sr-only` CSS class must remain in `app/assets/scss/global.scss`;
 * removing it will make the span visible to sighted users.
 */
export const NEW_TAB_LABEL = '(opens in new tab)'

function applyNewTab (el: HTMLElement, binding?: DirectiveBinding<string | undefined>): void {
  if (el.tagName !== 'A') { return }
  const existing = el.querySelector(':scope > [data-new-tab-hint="true"]')
  if (el.getAttribute('target') !== '_blank') {
    if (existing) { el.removeChild(existing) }
    return
  }
  const label = (binding?.value || NEW_TAB_LABEL)
  if (existing) {
    if (existing.textContent !== label) { existing.textContent = label }
    return
  }
  const span = document.createElement('span')
  span.className = 'sr-only'
  span.setAttribute('data-new-tab-hint', 'true')
  span.textContent = label
  el.appendChild(span)
}

export const newTabDirective: ObjectDirective<HTMLElement, string | undefined> = {
  mounted (el: HTMLElement, binding: DirectiveBinding<string | undefined>): void {
    applyNewTab(el, binding)
  },
  updated (el: HTMLElement, binding: DirectiveBinding<string | undefined>): void {
    applyNewTab(el, binding)
  }
}
